import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";

// Each link in the navbar. `slug` matches the collection page route at
// /collections/:slug. `slug: null` = the home / Featured page (top of /).
const NAV_CONFIG: Array<{ label: string; slug: string | null }> = [
  { label: "Featured", slug: null },
  { label: "Bedroom", slug: "bedroom" },
  { label: "Wall Art", slug: "wall-art" },
  { label: "Kitchenware", slug: "kitchenware" },
];

type NavbarProps = {
  /** When true (e.g. on a page with a full-bleed hero), the navbar starts transparent and turns solid on scroll. */
  transparentOverHero?: boolean;
};

export default function Navbar({ transparentOverHero = false }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalCount, open: openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    if (!transparentOverHero) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentOverHero]);

  // When floating over a hero and not scrolled, text/buttons go light.
  const isTransparent = transparentOverHero && !scrolled && !mobileOpen;

  const links = useMemo(() => {
    return NAV_CONFIG.map((item) => ({
      label: item.label,
      to: item.slug == null ? "/" : `/collections/${item.slug}`,
      slug: item.slug,
    }));
  }, []);

  // Active state.
  const activeKey = useMemo(() => {
    if (location.pathname.startsWith("/artisans")) return "artisans";
    if (location.pathname.startsWith("/collections/")) {
      const m = location.pathname.match(/^\/collections\/([^/]+)/);
      return m ? `slug:${m[1]}` : null;
    }
    if (location.pathname === "/") return "featured";
    return null;
  }, [location.pathname]);

  const isLinkActive = (link: { slug: string | null }) => {
    if (link.slug == null) return activeKey === "featured";
    return activeKey === `slug:${link.slug}`;
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        isTransparent
          ? "bg-transparent border-b border-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200/70"
      }`}
    >
      <div className="px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 gap-6">
          {/* Logo */}
          <Link
            to="/"
            className={`font-heading text-xl cursor-pointer whitespace-nowrap shrink-0 transition-colors ${
              isTransparent ? "text-background-50" : "text-foreground-950"
            }`}
          >
            Hearth &amp; Heirloom
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7 flex-1 justify-center">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  isTransparent
                    ? isLinkActive(l)
                      ? "text-background-50 font-medium"
                      : "text-background-50/80 hover:text-background-50"
                    : isLinkActive(l)
                      ? "text-foreground-950 font-medium"
                      : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/artisans"
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${
                isTransparent
                  ? activeKey === "artisans"
                    ? "text-background-50 font-medium"
                    : "text-background-50/80 hover:text-background-50"
                  : activeKey === "artisans"
                    ? "text-foreground-950 font-medium"
                    : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Artisans
            </Link>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              aria-label="Cart"
              onClick={openCart}
              className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm cursor-pointer whitespace-nowrap transition-colors ${
                isTransparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-950 hover:bg-background-100"
              }`}
            >
              Cart
              {totalCount > 0 && (
                <span
                  className={`inline-flex items-center justify-center text-[11px] min-w-5 h-5 px-1 rounded-full ${
                    isTransparent
                      ? "bg-background-50 text-foreground-950"
                      : "bg-foreground-950 text-background-50"
                  }`}
                >
                  {totalCount}
                </span>
              )}
            </button>
            <button
              type="button"
              aria-label="Menu"
              onClick={() => setMobileOpen((s) => !s)}
              className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                isTransparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-800 hover:bg-background-100"
              }`}
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-2 flex flex-col">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className={`py-3 text-sm border-b border-background-200/70 last:border-b-0 cursor-pointer ${
                  isLinkActive(l) ? "text-foreground-950 font-medium" : "text-foreground-800"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/artisans"
              onClick={() => setMobileOpen(false)}
              className={`py-3 text-sm border-b border-background-200/70 last:border-b-0 cursor-pointer ${
                activeKey === "artisans" ? "text-foreground-950 font-medium" : "text-foreground-800"
              }`}
            >
              Artisans
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}